import { GridValidRowModel } from "@mui/x-data-grid";
import { useState } from "react";
import { deleteSweepDestWallet } from "src/api/contractSweepRecipientSetting";
import DialogInOne from "src/components/DialogInOne";
import Prompter from "src/components/Prompter";
import { usePageConfigProviderContext } from "src/features/PageConfigProvider";
import { useAlerting, useTranslation, useYubiPrompt } from "src/hooks";
import { queryClient } from "src/utils/config";
import { appError } from "src/utils/errorHandler";
import { useZusDialogStore } from "src/zustand/store";
import { ViewGroupGetListRes } from "../types/ViewGroupGetListRes";

type Props = {
  isOpen: boolean;
  selectedRow:
    | (GridValidRowModel & { rawData: ViewGroupGetListRes })
    | undefined;
  onClose: () => void;
};

const useDeleteDialog = (props: Props) => {
  const { isOpen, selectedRow, onClose } = props;
  const { translatePrefix } = usePageConfigProviderContext();
  const { getResAfterYubi, prompterConfig } = useYubiPrompt();
  const zusDialog = useZusDialogStore();
  const { t } = useTranslation(translatePrefix);
  const [isDoubleConfirmDialogOpen, setIsDoubleConfirmDialogOpen] =
    useState(false);
  const { alerting } = useAlerting();

  //first delete
  const onConfirm = async () => {
    zusDialog.openExtra("loadingDialog");
    const res: any = await getResAfterYubi(
      deleteSweepDestWallet,
      {
        id: selectedRow?.rawData?.id,
        actionConfirmed: false,
      },
      {
        isSkipErrorHandling: true,
      },
    );
    zusDialog.closeExtra();

    if (!res) return;

    const { result = null, error = null, requestId = null } = res || {};

    if (error?.code === -69) {
      return alerting(
        "warning",
        t("the_last_sweep_dest_cannot_be_deleted_disabled"),
      );
    }

    if (!result || error) {
      return appError({
        ...error,
        requestId,
      });
    }

    if (result.double_confirm_required) {
      return setIsDoubleConfirmDialogOpen(true);
    }

    alerting("success", t("delete_record_success"));
    queryClient.invalidateQueries({
      queryKey: ["get_recipient_group_view_group"],
    });
    onClose();
  };

  //delete after double confirmed
  const onDoubeleConfirmDeleteYes = async () => {
    zusDialog.openExtra("loadingDialog");
    const res: any = await getResAfterYubi(deleteSweepDestWallet, {
      id: selectedRow?.rawData?.id,
      actionConfirmed: true,
    });
    zusDialog.closeExtra();

    if (!res) return;

    alerting("success", t("delete_record_success"));
    onDoubleConfirmDialogClose();
    queryClient.invalidateQueries({
      queryKey: ["get_recipient_group_view_group"],
    });
  };

  const onDoubleConfirmDialogClose = () => {
    setIsDoubleConfirmDialogOpen(false);
    onClose();
  };

  const renderDeleteDoubleConfirmDialog = () => {
    return (
      <DialogInOne
        title={t("please_double_confirm")}
        self={{
          open: isDoubleConfirmDialogOpen,
          onClose: onDoubleConfirmDialogClose,
        }}
        maxWidth="xs"
        content={<></>}
        onConfirm={onDoubeleConfirmDeleteYes}
        onCancel={onDoubleConfirmDialogClose}
      />
    );
  };

  const renderDeleteDialog = () => {
    return (
      <>
        <Prompter {...prompterConfig} />
        <DialogInOne
          title={t("confirm_to_delete")}
          self={{
            open: isOpen,
            onClose: onClose,
          }}
          maxWidth="xs"
          content={<></>}
          onConfirm={onConfirm}
          onCancel={onClose}
        />
      </>
    );
  };
  return { renderDeleteDialog, renderDeleteDoubleConfirmDialog };
};

export default useDeleteDialog;
